
import React, { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Heart, MessageCircle, MoreHorizontal, Edit, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { PostEditDialog } from './post/PostEditDialog';
import { PostDeleteButton } from './post/PostDeleteButton';
import { PostDetailDialog } from './PostDetailDialog';
import { PostImages } from './post/PostImages';
import { PostPoll } from './post/PostPoll';
import { usePostActions } from '@/hooks/usePostActions';
import { PostCardProps } from '@/types/community-props.types';
import { UserProfileHoverCard } from './UserProfileHoverCard';

const PREVIEW_LENGTH = 200;

export const PostCard = ({ post, onLikeChange, onPostDelete }: PostCardProps) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const { isLiked, likesCount, toggleLike, isLoading } = usePostActions({
    postId: post.id,
    initialLiked: post.is_liked || false,
    initialLikes: post.likes_count,
    onLikeChange
  });

  const getTruncatedContent = (content: string, maxLength: number) => {
    if (content.length <= maxLength) return content;
    const truncated = content.substring(0, maxLength).trim();
    const lastSpace = truncated.lastIndexOf(' ');
    return (lastSpace > 0 ? truncated.substring(0, lastSpace) : truncated) + '...';
  };

  const shouldShowReadMore = post.content.length > PREVIEW_LENGTH;

  const handleEditSuccess = async () => {
    if (onLikeChange) {
      await onLikeChange();
    }
  };

  const handleDeleteSuccess = () => {
    if (onPostDelete) {
      onPostDelete();
    }
  };

  return (
    <>
      <Card className="mb-4">
        <CardContent className="p-6">
          {/* Post Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={post.author?.logo_url || ''} />
                <AvatarFallback>
                  {post.author?.full_name?.charAt(0).toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              <div>
                <UserProfileHoverCard userId={post.author?.id ?? ''}>
                  <p className="font-semibold text-gray-900 hover:text-primary cursor-pointer transition-colors">
                    {post.author?.full_name || 'Usuário removido'}
                  </p>
                </UserProfileHoverCard>
                <p className="text-sm text-gray-500">
                  {formatDistanceToNow(new Date(post.created_at), {
                    addSuffix: true,
                    locale: ptBR
                  })}
                </p>
              </div>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setEditDialogOpen(true)}>
                  <Edit className="mr-2 h-4 w-4" />
                  Editar
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => setDeleteDialogOpen(true)}
                  className="text-red-600"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Excluir
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Post Content */}
          <div className="mb-4">
            <p className="text-gray-900 whitespace-pre-wrap">
              {getTruncatedContent(post.content, PREVIEW_LENGTH)}
            </p>
            {shouldShowReadMore && (
              <Button
                variant="link"
                onClick={() => setDetailDialogOpen(true)}
                className="text-primary p-0 h-auto font-normal mt-1"
              >
                Ver mais
              </Button>
            )}
          </div>

          {/* Post Images */}
          {post.image_urls && post.image_urls.length > 0 && (
            <div className="mb-4">
              <PostImages imageUrls={post.image_urls} />
            </div>
          )}

          {/* Post GIF */}
          {post.gif_url && (
            <div className="mb-4">
              <img src={post.gif_url} alt="GIF" className="rounded-lg max-h-64 w-auto" loading="lazy" />
            </div>
          )}

          {/* Post Poll */}
          <div className="mb-4">
            <PostPoll postId={post.id} />
          </div>

          {/* Post Actions */}
          <div className="flex items-center gap-4 pt-2 border-t">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLike}
              disabled={isLoading}
              className={`flex items-center gap-2 ${
                isLiked ? 'text-red-500' : 'text-gray-500'
              }`}
            >
              <Heart 
                className={`h-4 w-4 ${isLiked ? 'fill-current' : ''}`} 
              />
              <span>{likesCount}</span>
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDetailDialogOpen(true)}
              className="flex items-center gap-2 text-gray-500"
            >
              <MessageCircle className="h-4 w-4" />
              <span>{post.comments_count}</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <PostEditDialog
        post={post}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onSuccess={handleEditSuccess}
      />

      {/* Delete Dialog */}
      <PostDeleteButton
        post={post}
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onSuccess={handleDeleteSuccess}
      />

      {/* Detail Dialog */}
      <PostDetailDialog
        post={post}
        open={detailDialogOpen}
        onOpenChange={setDetailDialogOpen}
        onLikeChange={onLikeChange}
      />
    </>
  );
};
